import {MAP_ROTATION_ANGLE,POSITION_HISTORY_SIZE, MOVEMENT_THRESHOLD, TERMINAL_THRESHOLD, ROUTE_PROXIMITY_THRESHOLD, CONSTANTS} from './mapOptions'

export const calculateRealisticETA = (distance) => {
  const distanceInKm = distance / 1000;
  let baseTime = (distanceInKm / CONSTANTS.AVG_SPEED) * 3600;
  baseTime *= CONSTANTS.TRAFFIC_MULTIPLIER;
  const numTrafficLights = Math.floor(distance / 800);
  const avgTrafficLightDelay = 20;
  baseTime += numTrafficLights * avgTrafficLightDelay;
  return Math.min(
    Math.max(Math.round(baseTime), CONSTANTS.MIN_STOP_TIME),
    CONSTANTS.MAX_STOP_TIME
  );
};

// Existing calculateImprovedDistance (keep this as it's used elsewhere)
export const calculateImprovedDistance = (pos1, pos2) => {
  if (
    !pos1?.lat ||
    !pos1?.lng ||
    !pos2?.lat ||
    !pos2?.lng ||
    isNaN(pos1.lat) ||
    isNaN(pos1.lng) ||
    isNaN(pos2.lat) ||
    isNaN(pos2.lng)
  ) {
    console.warn("Invalid coordinates for distance calculation:", { pos1, pos2 });
    return null;
  }
  try {
    const φ1 = (parseFloat(pos1.lat) * Math.PI) / 180;
    const φ2 = (parseFloat(pos2.lat) * Math.PI) / 180;
    const Δφ = ((parseFloat(pos2.lat) - parseFloat(pos1.lat)) * Math.PI) / 180;
    const Δλ = ((parseFloat(pos2.lng) - parseFloat(pos1.lng)) * Math.PI) / 180;
    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = (CONSTANTS.EARTH_RADIUS || 6371000) * c;
    const minDistance = CONSTANTS.MIN_STOP_DISTANCE || 10;
    const maxDistance = CONSTANTS.MAX_STOP_DISTANCE || 100000;
    return Math.min(Math.max(distance, minDistance), maxDistance);
  } catch (error) {
    console.error("Error calculating distance:", error);
    return null;
  }
};
export const formatTime = (timeString) => {
  if (!timeString) return "---";
  if (
    typeof timeString === "string" &&
    (timeString.includes("m") || timeString.includes("s"))
  ) {
    return timeString;
  }
  const seconds =
    typeof timeString === "string" ? parseInt(timeString) : timeString;
  if (isNaN(seconds) || seconds <= 0) return "---";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

// New: calculateBearing function (this was missing but used)
export const calculateBearing = (p1, p2) => {
  if (
    !p1 ||
    !p2 ||
    isNaN(p1.latitude) ||
    isNaN(p1.longitude) ||
    isNaN(p2.latitude) ||
    isNaN(p2.longitude)
  ) {
    console.error("Invalid coordinates for bearing calculation:", p1, p2);
    return 0;
  }
  if (window.google && window.google.maps.geometry) {
    const from = new window.google.maps.LatLng(p1.latitude, p1.longitude);
    const to = new window.google.maps.LatLng(p2.latitude, p2.longitude);
    const bearing = window.google.maps.geometry.spherical.computeHeading(from, to);
    return (bearing + 360) % 360; // Normalize to 0-360
  }
  // Fallback to manual calculation
  const lat1 = (p1.latitude * Math.PI) / 180;
  const lon1 = (p1.longitude * Math.PI) / 180;
  const lat2 = (p2.latitude * Math.PI) / 180;
  const lon2 = (p2.longitude * Math.PI) / 180;
  const y = Math.sin(lon2 - lon1) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);
  const bearing = (Math.atan2(y, x) * 180) / Math.PI;
  return (bearing + 360) % 360;
};

// Existing calculateDistance (Haversine formula)
export const calculateDistance = (p1, p2) => {
  if (!p1 || !p2 || isNaN(p1.latitude) || isNaN(p1.longitude) || isNaN(p2.latitude) || isNaN(p2.longitude)) return Infinity;
  const R = 6371e3; // metres
  const φ1 = (p1.latitude * Math.PI) / 180; // φ, λ in radians
  const φ2 = (p2.latitude * Math.PI) / 180;
  const Δφ = ((p2.latitude - p1.latitude) * Math.PI) / 180;
  const Δλ = ((p2.longitude - p1.longitude) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // in meters
};

// New: Improved getOverallRouteBearing
export const getOverallRouteBearing = (routeCoordinates) => {
  if (!routeCoordinates || routeCoordinates.length < 2) return 0;

  let totalBearing = 0;
  let count = 0;
  for (let i = 0; i < routeCoordinates.length - 1; i++) {
    const p1 = {
      latitude: parseFloat(routeCoordinates[i].latitude),
      longitude: parseFloat(routeCoordinates[i].longitude),
    };
    const p2 = {
      latitude: parseFloat(routeCoordinates[i + 1].latitude),
      longitude: parseFloat(routeCoordinates[i + 1].longitude),
    };

    // Only calculate bearing for segments that are not extremely short
    // or if points are identical (to avoid NaN from atan2)
    const distance = calculateDistance(p1, p2);
    if (distance > 5) { // Only consider segments longer than 5 meters for bearing
      const bearing = calculateBearing(p1, p2);
      if (!isNaN(bearing)) { // Ensure bearing is a valid number
        totalBearing += bearing;
        count++;
      }
    }
  }
  return count > 0 ? totalBearing / count : 0;
};

export const normalizeDeviceData = (data) => {
  if (data.deviceId && data.latitude && data.longitude) {
    return data;
  }
  return {
    deviceId: data.device_id,
    latitude: data.Latitude,
    longitude: data.Longitude,
    timestamp: new Date().toISOString(),
    rawData: data.RawData || "",
  };
};


export const detectMovement = (newPos, lastPos) => {
  if (!lastPos || !newPos) return true;
  const distance = calculateDistance(
    { latitude: newPos.latitude, longitude: newPos.longitude },
    { latitude: lastPos.latitude, longitude: lastPos.longitude }
  );
  console.log(`[${newPos.deviceId}] Movement check: ${distance} meters`);
  return distance > MOVEMENT_THRESHOLD;
};

export const determineVehicleDirection = (
  deviceId,
  currentPosition,
  stops,
  routes,
  vehicles,
  positionHistory = []
) => {
  const storageKey = `${deviceId}_driver`;
  const storedData = JSON.parse(localStorage.getItem(storageKey) || "{}");
  let savedPositionHistory = storedData.positions || [];
  let lastMovedPosition = storedData.lastMovedPosition || null;
  let pathDirection = storedData.direction || "unknown";
  let speed = 0;

  // Validate stored data
  if (
    savedPositionHistory.length &&
    savedPositionHistory.some(
      (pos) => !pos.latitude || !pos.longitude || !pos.timestamp
    )
  ) {
    console.warn(`[${deviceId}] Clearing invalid stored position history`);
    savedPositionHistory = [];
    lastMovedPosition = null;
    pathDirection = "unknown";
  }

  const isMoving = detectMovement(currentPosition, lastMovedPosition);
  if (isMoving) {
    savedPositionHistory.push({
      ...currentPosition,
      timestamp: new Date().toISOString(),
    });
    lastMovedPosition = currentPosition;
    if (savedPositionHistory.length > POSITION_HISTORY_SIZE) {
      savedPositionHistory = savedPositionHistory.slice(-POSITION_HISTORY_SIZE);
    }
  }

  const saveToStorage = () => {
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        positions: savedPositionHistory,
        lastMovedPosition,
        direction: pathDirection,
        timestamp: Date.now(),
        deviceId,
      })
    );
  };

  if (!stops || stops.length < 2 || !routes || routes.length === 0) {
    console.warn(
      `[${deviceId}] Invalid data: stops=${stops?.length}, routes=${routes?.length}`
    );
    saveToStorage();
    return { direction: pathDirection, speed };
  }

  const vehicle = vehicles.find((v) => v.device_id === deviceId);
  if (!vehicle || !vehicle.routeId) {
    console.warn(`[${deviceId}] No vehicle or routeId found`);
    saveToStorage();
    return { direction: pathDirection, speed };
  }

  const vehicleRoute = routes.find((r) => r.id === vehicle.routeId);
  if (
    !vehicleRoute ||
    !vehicleRoute.routeCoordinates ||
    vehicleRoute.routeCoordinates.length === 0
  ) {
    console.warn(`[${deviceId}] No valid route found`);
    saveToStorage();
    return { direction: pathDirection, speed };
  }

  // Check if vehicle is on route
  const isOnRoute = vehicleRoute.routeCoordinates.some(
    (coord) =>
      calculateDistance(
        {
          latitude: parseFloat(coord.latitude),
          longitude: parseFloat(coord.longitude),
        },
        currentPosition
      ) < ROUTE_PROXIMITY_THRESHOLD
  );
  if (!isOnRoute) {
    console.warn(
      `[${deviceId}] Vehicle not on route: min distance > ${ROUTE_PROXIMITY_THRESHOLD}m`
    );
    saveToStorage();
    return { direction: pathDirection, speed };
  }

  const source = {
    latitude: parseFloat(stops[0].latitude),
    longitude: parseFloat(stops[0].longitude),
  };
  const destination = {
    latitude: parseFloat(stops[stops.length - 1].latitude),
    longitude: parseFloat(stops[stops.length - 1].longitude),
  };

  const isRoundTrip =
    calculateDistance(source, destination) < TERMINAL_THRESHOLD;

  // Calculate speed and bearing if moving
  if (isMoving && savedPositionHistory.length >= 2) {
    const prevPos = savedPositionHistory[savedPositionHistory.length - 2];
    const timeDiff =
      (new Date(currentPosition.timestamp) - new Date(prevPos.timestamp)) / 1000;
    const distance = calculateDistance(currentPosition, prevPos);
    speed = timeDiff > 0 ? (distance / timeDiff) * 3.6 : 0; // Convert to km/h

    const bearing = calculateBearing(prevPos, currentPosition);
    const routeBearing = getOverallRouteBearing(vehicleRoute.routeCoordinates);

    // Determine direction based on bearing alignment
    const bearingDiff = Math.abs(
      ((bearing - routeBearing + 180) % 360) - 180
    );
    if (bearingDiff < 90) {
      pathDirection = "source_to_dest";
    } else if (bearingDiff > 270) {
      pathDirection = "dest_to_source";
    }
  }

  // Terminal-based direction switching
  const sourceDistance = calculateDistance(currentPosition, source);
  const destDistance = calculateDistance(currentPosition, destination);
  const nearSource = sourceDistance < TERMINAL_THRESHOLD;
  const nearDest = destDistance < TERMINAL_THRESHOLD;

  if (isRoundTrip && (nearSource || nearDest)) {
    if (pathDirection === "source_to_dest") {
      console.log(`[${deviceId}] At terminal, toggling to dest_to_source`);
      pathDirection = "dest_to_source";
      savedPositionHistory = [currentPosition];
    } else if (pathDirection === "dest_to_source") {
      console.log(`[${deviceId}] At terminal, toggling to source_to_dest`);
      pathDirection = "source_to_dest";
      savedPositionHistory = [currentPosition];
    }
  } else {
    if (nearSource && pathDirection !== "source_to_dest") {
      console.log(`[${deviceId}] At source, setting source_to_dest`);
      pathDirection = "source_to_dest";
      savedPositionHistory = [currentPosition];
    } else if (nearDest && pathDirection !== "dest_to_source") {
      console.log(`[${deviceId}] At destination, setting dest_to_source`);
      pathDirection = "dest_to_source";
      savedPositionHistory = [currentPosition];
    }
  }

  // Trend-based direction confirmation
  if (isMoving && savedPositionHistory.length >= POSITION_HISTORY_SIZE) {
    let sourceDistanceChange = 0;
    let destDistanceChange = 0;
    for (let i = 1; i < savedPositionHistory.length; i++) {
      const prevPos = savedPositionHistory[i - 1];
      const currPos = savedPositionHistory[i];
      sourceDistanceChange +=
        calculateDistance(currPos, source) - calculateDistance(prevPos, source);
      destDistanceChange +=
        calculateDistance(currPos, destination) -
        calculateDistance(prevPos, destination);
    }
    console.log(
      `[${deviceId}] Trend: sourceΔ=${sourceDistanceChange}, destΔ=${destDistanceChange}`
    );
    let newDirection = null;
    if (sourceDistanceChange < 0 && destDistanceChange > 0) {
      newDirection = "dest_to_source";
    } else if (sourceDistanceChange > 0 && destDistanceChange < 0) {
      newDirection = "source_to_dest";
    }
    if (newDirection && newDirection !== pathDirection) {
      console.log(`[${deviceId}] Updating to ${newDirection} via trend`);
      pathDirection = newDirection;
      savedPositionHistory = savedPositionHistory.slice(-3);
    }
  }

  saveToStorage();
  return { direction: pathDirection, speed, bearing: isMoving ? calculateBearing(savedPositionHistory[savedPositionHistory.length - 2], currentPosition) : 0 };
};

export const calculateLabelOffsets = (positions) => {
  const offsets = new Array(positions.length).fill(0);
  const labelHeight = 30; // Approximate height of a label in pixels

  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const pos1 = positions[i];
      const pos2 = positions[j];
      const distance = calculateImprovedDistance(pos1, pos2);
      if (distance < 0.001) {
        // Adjust threshold based on your map scale
        offsets[j] += labelHeight; // Stack vertically
      }
    }
  }

  return offsets;
};

export const calculateMapRotation = (source, destination) => {
  if (!source || !destination) return 0;
  
  try {
    const lat1 = parseFloat(source.latitude);
    const lng1 = parseFloat(source.longitude);
    const lat2 = parseFloat(destination.latitude);
    const lng2 = parseFloat(destination.longitude);
    
    if (isNaN(lat1) || isNaN(lng1) || isNaN(lat2) || isNaN(lng2)) {
      console.warn('Invalid coordinates for rotation:', { source, destination });
      return 0;
    }

    // Calculate angle in degrees
    const dy = lat2 - lat1;
    const dx = lng2 - lng1;
    let angle = Math.atan2(dy, dx) * (180 / Math.PI);
    
    // Adjust angle so that source->destination points upward (0 degrees)
    angle = 90 - angle;
    
    // Normalize angle to 0-360
    angle = (angle + 360) % 360;
    
    return angle;
  } catch (error) {
    console.error('Error calculating map rotation:', error);
    return 0;
  }
};